import { effect, inject, Injectable, Injector, linkedSignal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { PusherUserEvent } from '../../shared/models/enums/pusher.enums';

import { totalUnreadWebsiteNotificationsEventSchema } from '../../shared/models/schemas/website-notification.schema';
import { apiQuery } from '../api/api-query';
import * as AuthSelectors from '../state/auth-state/auth.selectors';
import { HttpNotificationService } from './http/http-notification.service';
import { PusherUserChannelService } from './pusher-user-channel.service';
import { PusherService } from './pusher.service';

@Injectable({
  providedIn: 'root',
})
export class WebsiteNotificationsService {
  private injector = inject(Injector);
  private store = inject(Store);
  private httpNotificationService = inject(HttpNotificationService);
  private pusherService = inject(PusherService);
  private pusherUserChannelService = inject(PusherUserChannelService);

  private isLogged = toSignal(this.store.select(AuthSelectors.isLogged));

  private totalUnreadQuery = apiQuery(
    () => this.httpNotificationService.getMyTotalUnreadWebsiteNotifications(),
    { onError: (apiError) => console.error('compteur de notifications non lues', apiError) }
  );

  readonly totalUnreadWebsiteNotifications = linkedSignal(() => this.totalUnreadQuery.value() ?? 0);

  private lastPushedTotalUnread: number | null = null;
  private lastPushedAt = 0;

  init() {
    this.pusherUserChannelService.bind(
      PusherUserEvent.WEBSITE_NOTIFICATION_COUNT_UPDATED,
      totalUnreadWebsiteNotificationsEventSchema,
      (totalUnread) => {
        this.lastPushedTotalUnread = totalUnread;
        this.lastPushedAt = Date.now();
        this.totalUnreadWebsiteNotifications.set(totalUnread);
      }
    );

    this.pusherService.onResynchronize(() => {
      if (this.isLogged()) {
        this.countUnreadWebsiteNotifications();
      }
    });

    effect(
      () => {
        if (this.isLogged()) {
          untracked(() => this.countUnreadWebsiteNotifications());
        } else {
          this.totalUnreadWebsiteNotifications.set(0);
        }
      },
      { injector: this.injector }
    );
  }

  async countUnreadWebsiteNotifications() {
    const startedAt = Date.now();
    const totalUnread = await this.totalUnreadQuery.load();
    if (totalUnread === null) return;

    // un event reçu pendant la requête porte une valeur plus récente que la réponse REST
    if (this.lastPushedAt > startedAt) {
      this.totalUnreadWebsiteNotifications.set(this.lastPushedTotalUnread!);
      return;
    }

    this.totalUnreadWebsiteNotifications.set(totalUnread);
  }
}
